import { useEffect, useRef } from "react";
import { NOTIFICATIONS_SOCKET_RECONNECTED_EVENT } from "../../helper/WebSocketService";
import {
  DEFAULT_PAGE_SIZE,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
} from "../../store/apiSlice/notificationApiSlice";

/** Ignore reconnect storms — at most one backup list fetch per this window. */
const RESYNC_MIN_INTERVAL_MS = 15000;

const NO_AUTO_REFETCH = {
  refetchOnFocus: false,
  refetchOnMountOrArgChange: false,
  refetchOnReconnect: false,
};

/**
 * Loads the notification inbox once per signed-in session; the socket keeps it live.
 *
 * One list call returns page 1 and ``unread_count``. Backup is list-only and runs
 * only after an unexpected socket drop (not tab focus, not route changes).
 *
 * @return {null}
 */
export function NotificationInboxBootstrap() {
  useGetNotificationsQuery({ limit: DEFAULT_PAGE_SIZE, offset: 0 }, NO_AUTO_REFETCH);

  const [refreshNotifications] = useLazyGetNotificationsQuery();
  const refreshRef = useRef(refreshNotifications);
  const lastResyncAtRef = useRef(0);

  useEffect(() => {
    refreshRef.current = refreshNotifications;
  }, [refreshNotifications]);

  useEffect(() => {
    const onSocketFailedAndReconnected = () => {
      const now = Date.now();
      if (now - lastResyncAtRef.current < RESYNC_MIN_INTERVAL_MS) {
        return;
      }
      lastResyncAtRef.current = now;
      refreshRef.current({ limit: DEFAULT_PAGE_SIZE, offset: 0 });
    };

    window.addEventListener(NOTIFICATIONS_SOCKET_RECONNECTED_EVENT, onSocketFailedAndReconnected);
    return () => {
      window.removeEventListener(
        NOTIFICATIONS_SOCKET_RECONNECTED_EVENT,
        onSocketFailedAndReconnected,
      );
    };
  }, []);

  return null;
}
